import {
  createCardArt,
  createVerseCardFace,
  type VerseCardFaceOptions,
} from "./cardFace";

/** Ability pill with optional rules text (hover / click). */
export type KeywordPillData = {
  id?: string;
  label: string;
  /** Full rules / reminder shown on hover and click */
  reminder?: string;
};

export type CardFullscreenData = Omit<VerseCardFaceOptions, "size" | "selected"> & {
  /** Full verse text (KJV) under the art on the study card */
  textKjv?: string;
  /**
   * Line spoken on play (Proclaim). Matched into textKjv and highlighted
   * when study layout shows the full verse.
   */
  proclaimText?: string;
  /** Mechanical / study blurb in the right panel */
  effectSummary?: string;
  /** Extra stat rows (e.g. speak duration, role) in the right panel */
  detailRows?: { label: string; value: string }[];
  /**
   * Named abilities with reminders (preferred over keywordLabels alone).
   * Hover or click a pill to read the effect.
   */
  keywords?: KeywordPillData[];
  /**
   * When true (default if textKjv or effectSummary set):
   * left study card (art + overlays + verse) + right info card.
   * When false: legacy single-card enlarge.
   */
  studyLayout?: boolean;
};

type TextRange = { start: number; end: number };

type WordToken = {
  start: number;
  end: number;
  norm: string;
};

function normWord(w: string): string {
  return w.toLowerCase().replace(/[^a-z0-9']/g, "");
}

function tokenizeWords(s: string): WordToken[] {
  const out: WordToken[] = [];
  const re = /\S+/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s))) {
    const norm = normWord(m[0]);
    if (!norm) continue;
    out.push({ start: m.index, end: m.index + m[0].length, norm });
  }
  return out;
}

/**
 * Find character ranges in `full` that match word runs from `spoken`
 * (order-preserving, allows gaps for shortened / multi-clause proclaim lines).
 */
function findSpokenRanges(full: string, spoken: string): TextRange[] {
  const fullToks = tokenizeWords(full);
  const spokenToks = tokenizeWords(spoken);
  if (!fullToks.length || !spokenToks.length) return [];

  const fullNorm = fullToks.map((t) => t.norm).join(" ");
  const spokenNorm = spokenToks.map((t) => t.norm).join(" ");
  // Exact whole-line match (normalized): highlight entire verse body span
  if (fullNorm === spokenNorm) {
    return [
      {
        start: fullToks[0]!.start,
        end: fullToks[fullToks.length - 1]!.end,
      },
    ];
  }

  const ranges: TextRange[] = [];
  let sIdx = 0;
  let searchFrom = 0;
  const minRun = spokenToks.length <= 4 ? 1 : 2;

  while (sIdx < spokenToks.length) {
    let found = false;
    // Prefer longest match of remaining spoken words
    for (let len = spokenToks.length - sIdx; len >= minRun; len--) {
      for (let f = searchFrom; f <= fullToks.length - len; f++) {
        let ok = true;
        for (let k = 0; k < len; k++) {
          if (fullToks[f + k]!.norm !== spokenToks[sIdx + k]!.norm) {
            ok = false;
            break;
          }
        }
        if (ok) {
          ranges.push({
            start: fullToks[f]!.start,
            end: fullToks[f + len - 1]!.end,
          });
          sIdx += len;
          searchFrom = f + len;
          found = true;
          break;
        }
      }
      if (found) break;
    }
    if (!found) {
      // Paraphrase / reordered word — skip and keep searching
      sIdx++;
    }
  }

  return mergeRanges(ranges);
}

function mergeRanges(ranges: TextRange[]): TextRange[] {
  if (!ranges.length) return [];
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const out: TextRange[] = [{ ...sorted[0]! }];
  for (let i = 1; i < sorted.length; i++) {
    const r = sorted[i]!;
    const last = out[out.length - 1]!;
    // Merge if overlapping or only whitespace between
    if (r.start <= last.end + 2) {
      last.end = Math.max(last.end, r.end);
    } else {
      out.push({ ...r });
    }
  }
  return out;
}

/** Build verse blockquote with spoken portions wrapped in <mark class="card-fs-spoken">. */
function buildVerseTextEl(fullKjv: string, proclaimText?: string): HTMLElement {
  const verse = document.createElement("blockquote");
  verse.className = "card-fs-verse-text";

  const full = fullKjv.replace(/\s+/g, " ").trim();
  const spoken = proclaimText?.replace(/\s+/g, " ").trim();

  if (!spoken) {
    verse.textContent = full;
    return verse;
  }

  const ranges = findSpokenRanges(full, spoken);
  if (!ranges.length) {
    // Can't align — show full verse, then spoken line for study
    verse.appendChild(document.createTextNode(full));
    const note = document.createElement("p");
    note.className = "card-fs-spoken-fallback";
    const label = document.createElement("span");
    label.className = "card-fs-spoken-label";
    label.textContent = "Spoken: ";
    const mark = document.createElement("mark");
    mark.className = "card-fs-spoken";
    mark.textContent = spoken;
    note.append(label, mark);
    verse.appendChild(note);
    return verse;
  }

  let cursor = 0;
  for (const r of ranges) {
    if (r.start > cursor) {
      verse.appendChild(document.createTextNode(full.slice(cursor, r.start)));
    }
    const mark = document.createElement("mark");
    mark.className = "card-fs-spoken";
    mark.textContent = full.slice(r.start, r.end);
    mark.title = "Spoken when this card is played";
    verse.appendChild(mark);
    cursor = r.end;
  }
  if (cursor < full.length) {
    verse.appendChild(document.createTextNode(full.slice(cursor)));
  }
  return verse;
}

/** Optional spoken-Word preview for study (wired from prototype / deck audio). */
export type CardFullscreenVoPreview = {
  play: (verseId: string) => Promise<boolean>;
  stop: () => void;
  isPlaying: (verseId?: string) => boolean;
};

/**
 * Full-screen card lightbox. Self-mounts a backdrop; one global instance.
 * ESC, backdrop click, or Close dismisses.
 *
 * Study layout:
 * - Left card: 1:1 art from top; ref + cost overlay top; effect pills overlay bottom;
 *   verse text under the image (left-aligned, vertically centered).
 * - Right card: meta, stats, and in-play effect summary.
 */
export class CardFullscreen {
  private root: HTMLElement | null = null;
  private mount: HTMLElement | null = null;
  private details: HTMLElement | null = null;
  private keyHandler: ((e: KeyboardEvent) => void) | null = null;
  /** Floating ability tooltip (does not reflow card panels) */
  private tipEl: HTMLElement | null = null;
  private tipTitle: HTMLElement | null = null;
  private tipBody: HTMLElement | null = null;
  private activePill: HTMLElement | null = null;
  private tipPinned = false;
  private tipHideTimer: number | null = null;
  private tipOutsideHandler: ((e: PointerEvent) => void) | null = null;
  private tipRepositionHandler: (() => void) | null = null;
  /** Study VO — set from main/deck after audio unlock path exists. */
  private voPreview: CardFullscreenVoPreview | null = null;
  private voBtn: HTMLButtonElement | null = null;
  private voVerseId = "";
  /** True only when this lightbox started the current VO (safe to stop on close). */
  private voOwned = false;
  private voPollTimer: number | null = null;
  /** Removes temporary click-through guards after backdrop dismiss. */
  private clickThroughCleanup: (() => void) | null = null;

  /**
   * Wire spoken-Word preview (Proclaim VO clips). Call once from main / deck page.
   * Pass null to unbind.
   */
  setVoPreview(provider: CardFullscreenVoPreview | null): void {
    if (!provider && this.voOwned) {
      this.voPreview?.stop();
      this.voOwned = false;
    }
    this.voPreview = provider;
    this.syncVoButton();
  }

  isOpen(): boolean {
    return !!this.root && !this.root.classList.contains("hidden");
  }

  show(data: CardFullscreenData): void {
    this.ensureDom();
    const root = this.root!;
    const mount = this.mount!;
    const details = this.details!;

    // New card → stop any VO we started for the previous one
    this.stopOwnedVo();

    const pills = this.normalizeKeywords(data);
    const study =
      data.studyLayout !== false &&
      !!(
        data.textKjv ||
        data.effectSummary ||
        data.detailRows?.length ||
        pills.length
      );

    mount.replaceChildren();
    details.replaceChildren();
    this.hideKeywordTip();
    details.classList.toggle("hidden", !study);
    root.classList.toggle("has-study", study);

    this.voVerseId = data.verseId;
    this.voBtn = null;

    if (study) {
      mount.appendChild(this.buildStudyCard(data, pills));
      this.fillDetailPanel(details, data);
    } else {
      const face = createVerseCardFace({
        verseId: data.verseId,
        reference: data.reference,
        category: data.category,
        speakSeconds: data.speakSeconds,
        metaLine: data.metaLine,
        effectLine: data.effectLine,
        keywordLabels:
          data.keywordLabels ?? pills.map((p) => p.label),
        size: "fullscreen",
      });
      mount.appendChild(face);
    }

    root.classList.remove("hidden");
    document.body.classList.add("card-fs-open");
    this.bindKeys();
    this.syncVoButton();
  }

  hide(): void {
    if (!this.root) return;
    this.stopOwnedVo();
    this.root.classList.add("hidden");
    this.mount?.replaceChildren();
    this.details?.replaceChildren();
    this.hideKeywordTip();
    document.body.classList.remove("card-fs-open");
    this.unbindKeys();
    this.voBtn = null;
    this.voVerseId = "";
  }

  /**
   * After closing on pointerdown (backdrop), the same gesture often synthesizes
   * pointerup + click on whatever is under the finger/cursor. Without a guard
   * that re-opens study on campaign tiles, hand art, deck faces, etc.
   */
  private suppressClickThrough(): void {
    this.clickThroughCleanup?.();
    const block = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    };
    const opts: AddEventListenerOptions = { capture: true };
    const types = [
      "pointerup",
      "mouseup",
      "click",
      "auxclick",
      "touchend",
    ] as const;
    for (const type of types) {
      window.addEventListener(type, block, opts);
    }
    const timer = window.setTimeout(() => cleanup(), 450);
    const cleanup = () => {
      window.clearTimeout(timer);
      for (const type of types) {
        window.removeEventListener(type, block, opts);
      }
      if (this.clickThroughCleanup === cleanup) this.clickThroughCleanup = null;
    };
    this.clickThroughCleanup = cleanup;
  }

  private normalizeKeywords(data: CardFullscreenData): KeywordPillData[] {
    if (data.keywords?.length) return data.keywords;
    if (data.keywordLabels?.length) {
      return data.keywordLabels.map((label) => ({ label }));
    }
    return [];
  }

  /**
   * Left study card:
   * ┌ book / 4:6–7  cost ┐  ← nameplate on art
   * │     1:1 image      │
   * │ [pill] [pill]      │  ← overlay on art (hover/click for rules)
   * ├────────────────────┤
   * │  verse text…       │  ← under image, left + v-center
   * └────────────────────┘
   */
  private buildStudyCard(
    data: CardFullscreenData,
    pills: KeywordPillData[],
  ): HTMLElement {
    const card = document.createElement("article");
    card.className = `card-fs-study category-${data.category}`;
    card.dataset.verseId = data.verseId;

    const stage = document.createElement("div");
    stage.className = "card-fs-art-stage";

    const art = createCardArt({
      verseId: data.verseId,
      category: data.category,
      reference: data.reference,
      speakSeconds: data.speakSeconds,
      compactRef: false,
    });
    art.classList.add("card-fs-art");

    stage.append(art);

    if (pills.length) {
      const row = document.createElement("div");
      row.className = "card-fs-overlay-pills keyword-row";
      row.setAttribute("role", "list");
      row.setAttribute("aria-label", "Named abilities");

      for (const kw of pills.slice(0, 6)) {
        const chip = document.createElement("button");
        chip.type = "button";
        chip.className =
          "keyword-chip card-fs-pill" +
          (kw.id ? ` kw-${kw.id}` : "");
        chip.textContent = kw.label;
        chip.setAttribute("role", "listitem");
        if (kw.reminder) {
          // Prefer floating tip over native title (title steals hover)
          chip.setAttribute(
            "aria-label",
            `${kw.label}. ${kw.reminder}`,
          );
          chip.addEventListener("pointerenter", () => {
            this.cancelTipHide();
            if (!this.tipPinned) this.showKeywordTip(kw, chip, false);
          });
          chip.addEventListener("pointerleave", (e) => {
            if (this.tipPinned) return;
            const related = e.relatedTarget;
            if (
              related instanceof Node &&
              (this.tipEl?.contains(related) || chip.contains(related))
            ) {
              return;
            }
            this.scheduleTipHide();
          });
          chip.addEventListener("focus", () => {
            this.showKeywordTip(kw, chip, false);
          });
          chip.addEventListener("blur", () => {
            if (!this.tipPinned) this.scheduleTipHide();
          });
          chip.addEventListener("click", (e) => {
            e.stopPropagation();
            e.preventDefault();
            // Toggle pin on same pill
            if (this.tipPinned && this.activePill === chip) {
              this.hideKeywordTip();
              return;
            }
            this.showKeywordTip(kw, chip, true);
          });
        } else {
          chip.title = kw.label;
          chip.disabled = true;
          chip.classList.add("no-reminder");
        }
        row.appendChild(chip);
      }
      stage.appendChild(row);
    }

    const verseBand = document.createElement("div");
    verseBand.className = "card-fs-verse-band";

    if (data.textKjv) {
      verseBand.appendChild(
        buildVerseTextEl(data.textKjv, data.proclaimText),
      );
    } else if (data.proclaimText) {
      // Spoken-only fallback (no full KJV provided)
      verseBand.appendChild(buildVerseTextEl(data.proclaimText, data.proclaimText));
    } else {
      verseBand.classList.add("empty");
    }

    // Hear spoken Word — corner control inside the verse band (not a text row)
    const voBtn = document.createElement("button");
    voBtn.type = "button";
    voBtn.className = "card-fs-vo-btn";
    voBtn.dataset.verseId = data.verseId;
    voBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      e.preventDefault();
      void this.toggleVoPreview(data.verseId, voBtn);
    });
    this.voBtn = voBtn;
    verseBand.appendChild(voBtn);
    if (!data.textKjv && !data.proclaimText) {
      verseBand.classList.remove("empty");
    }

    card.append(stage, verseBand);
    return card;
  }

  private stopOwnedVo(): void {
    this.clearVoPoll();
    if (this.voOwned) {
      this.voPreview?.stop();
      this.voOwned = false;
    }
    this.syncVoButton();
  }

  private clearVoPoll(): void {
    if (this.voPollTimer != null) {
      window.clearInterval(this.voPollTimer);
      this.voPollTimer = null;
    }
  }

  private startVoPoll(): void {
    this.clearVoPoll();
    this.voPollTimer = window.setInterval(() => {
      if (!this.voOwned) {
        this.clearVoPoll();
        this.syncVoButton();
        return;
      }
      const playing =
        !!this.voPreview?.isPlaying(this.voVerseId || undefined);
      if (!playing) {
        this.voOwned = false;
        this.clearVoPoll();
        this.syncVoButton();
      } else {
        this.syncVoButton();
      }
    }, 200);
  }

  private syncVoButton(): void {
    const btn = this.voBtn;
    if (!btn) return;
    const provider = this.voPreview;
    const verseId = btn.dataset.verseId || this.voVerseId;

    if (!provider || !verseId) {
      btn.hidden = true;
      return;
    }

    btn.hidden = false;
    btn.disabled = false;
    btn.classList.remove("is-loading", "is-playing", "is-missing");

    if (this.voOwned && provider.isPlaying(verseId)) {
      btn.classList.add("is-playing");
      btn.setAttribute("aria-label", "Stop spoken verse");
      btn.title = "Stop";
      btn.innerHTML =
        `<span class="card-fs-vo-icon" aria-hidden="true">■</span>` +
        `<span class="card-fs-vo-label">Stop</span>`;
      return;
    }

    if (btn.dataset.loading === "1") {
      btn.classList.add("is-loading");
      btn.disabled = true;
      btn.setAttribute("aria-label", "Loading spoken verse");
      btn.title = "Loading…";
      btn.innerHTML =
        `<span class="card-fs-vo-icon" aria-hidden="true">…</span>` +
        `<span class="card-fs-vo-label">Loading</span>`;
      return;
    }

    if (btn.dataset.missing === "1") {
      btn.classList.add("is-missing");
      btn.disabled = true;
      btn.setAttribute("aria-label", "No spoken recording for this verse");
      btn.title = "No recording yet";
      btn.innerHTML =
        `<span class="card-fs-vo-icon" aria-hidden="true">◌</span>` +
        `<span class="card-fs-vo-label">No VO</span>`;
      return;
    }

    btn.setAttribute("aria-label", "Hear spoken verse");
    btn.title = "Hear the line spoken when this card is played";
    btn.innerHTML =
      `<span class="card-fs-vo-icon" aria-hidden="true">▶</span>` +
      `<span class="card-fs-vo-label">Hear</span>`;
  }

  private async toggleVoPreview(
    verseId: string,
    btn: HTMLButtonElement,
  ): Promise<void> {
    const provider = this.voPreview;
    if (!provider || !verseId) return;

    if (this.voOwned && provider.isPlaying(verseId)) {
      provider.stop();
      this.voOwned = false;
      this.clearVoPoll();
      btn.dataset.loading = "";
      btn.dataset.missing = "";
      this.syncVoButton();
      return;
    }

    // Stop any prior owned preview before starting another
    if (this.voOwned) {
      provider.stop();
      this.voOwned = false;
    }

    btn.dataset.loading = "1";
    btn.dataset.missing = "";
    this.syncVoButton();

    let ok = false;
    try {
      ok = await provider.play(verseId);
    } catch {
      ok = false;
    }

    btn.dataset.loading = "";
    if (!ok) {
      btn.dataset.missing = "1";
      this.voOwned = false;
      this.syncVoButton();
      return;
    }

    this.voOwned = true;
    this.voVerseId = verseId;
    this.startVoPoll();
    this.syncVoButton();
  }

  private ensureTipDom(): void {
    if (this.tipEl) return;
    const tip = document.createElement("div");
    tip.className = "card-fs-kw-tip hidden";
    tip.setAttribute("role", "tooltip");
    const title = document.createElement("div");
    title.className = "card-fs-kw-tip-title";
    const body = document.createElement("p");
    body.className = "card-fs-kw-tip-body";
    tip.append(title, body);
    tip.addEventListener("pointerenter", () => this.cancelTipHide());
    tip.addEventListener("pointerleave", () => {
      if (!this.tipPinned) this.scheduleTipHide();
    });
    // Append inside fullscreen root so z-index stays above backdrop
    (this.root ?? document.body).appendChild(tip);
    this.tipEl = tip;
    this.tipTitle = title;
    this.tipBody = body;
  }

  private showKeywordTip(
    kw: KeywordPillData,
    chip: HTMLElement,
    pin: boolean,
  ): void {
    if (!kw.reminder) return;
    this.ensureTipDom();
    const tip = this.tipEl!;
    this.cancelTipHide();
    this.tipPinned = pin;
    tip.classList.toggle("is-pinned", pin);

    if (this.activePill && this.activePill !== chip) {
      this.activePill.classList.remove("is-active");
    }
    this.activePill = chip;
    chip.classList.add("is-active");

    this.tipTitle!.textContent = kw.label;
    this.tipBody!.textContent = kw.reminder;
    tip.classList.remove("hidden");
    this.positionTipNear(chip);
    this.bindTipOutside();
  }

  private positionTipNear(chip: HTMLElement): void {
    const tip = this.tipEl;
    if (!tip || tip.classList.contains("hidden")) return;
    const r = chip.getBoundingClientRect();
    const pad = 8;
    const tw = tip.offsetWidth || 240;
    const th = tip.offsetHeight || 80;

    // Prefer above the pill; fall back below if clipped
    let top = r.top - th - pad;
    if (top < pad) top = r.bottom + pad;

    let left = r.left + r.width / 2 - tw / 2;
    left = Math.max(pad, Math.min(left, window.innerWidth - tw - pad));
    top = Math.max(pad, Math.min(top, window.innerHeight - th - pad));

    tip.style.left = `${Math.round(left)}px`;
    tip.style.top = `${Math.round(top)}px`;
  }

  private hideKeywordTip(): void {
    this.cancelTipHide();
    this.tipPinned = false;
    if (this.activePill) {
      this.activePill.classList.remove("is-active");
      this.activePill = null;
    }
    if (this.tipEl) {
      this.tipEl.classList.add("hidden");
      this.tipEl.classList.remove("is-pinned");
    }
    this.unbindTipOutside();
  }

  private scheduleTipHide(): void {
    this.cancelTipHide();
    this.tipHideTimer = window.setTimeout(() => this.hideKeywordTip(), 120);
  }

  private cancelTipHide(): void {
    if (this.tipHideTimer != null) {
      window.clearTimeout(this.tipHideTimer);
      this.tipHideTimer = null;
    }
  }

  private bindTipOutside(): void {
    if (this.tipOutsideHandler) return;
    this.tipOutsideHandler = (e: PointerEvent) => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (this.tipEl?.contains(t)) return;
      if (t instanceof Element && t.closest(".card-fs-pill")) return;
      this.hideKeywordTip();
    };
    this.tipRepositionHandler = () => {
      if (this.activePill) this.positionTipNear(this.activePill);
    };
    document.addEventListener("pointerdown", this.tipOutsideHandler, true);
    window.addEventListener("resize", this.tipRepositionHandler);
    window.addEventListener("scroll", this.tipRepositionHandler, true);
  }

  private unbindTipOutside(): void {
    if (this.tipOutsideHandler) {
      document.removeEventListener("pointerdown", this.tipOutsideHandler, true);
      this.tipOutsideHandler = null;
    }
    if (this.tipRepositionHandler) {
      window.removeEventListener("resize", this.tipRepositionHandler);
      window.removeEventListener("scroll", this.tipRepositionHandler, true);
      this.tipRepositionHandler = null;
    }
  }

  /** Right info card: meta, stats, effect summary (stable size — tips are floating) */
  private fillDetailPanel(
    details: HTMLElement,
    data: CardFullscreenData,
  ): void {
    details.className = `card-fullscreen-details card-fs-info-card category-${data.category}`;

    const h = document.createElement("h2");
    h.className = "card-fs-detail-title";
    h.textContent = data.reference;
    details.appendChild(h);

    if (data.metaLine) {
      const meta = document.createElement("p");
      meta.className = "card-fs-detail-meta";
      meta.textContent = data.metaLine;
      details.appendChild(meta);
    }

    if (data.detailRows?.length) {
      const dl = document.createElement("dl");
      dl.className = "card-fs-detail-stats";
      for (const row of data.detailRows) {
        const dt = document.createElement("dt");
        dt.textContent = row.label;
        const dd = document.createElement("dd");
        dd.textContent = row.value;
        dl.append(dt, dd);
      }
      details.appendChild(dl);
    }

    // Abilities as one compact paragraph: *Label* explanation. *Label* …
    const pills = this.normalizeKeywords(data).filter((k) => k.reminder);
    if (pills.length) {
      const ablLabel = document.createElement("p");
      ablLabel.className = "card-fs-section-label";
      ablLabel.textContent = "In play";
      details.appendChild(ablLabel);

      const para = document.createElement("p");
      para.className = "card-fs-effect card-fs-in-play";
      para.setAttribute("role", "text");

      for (let i = 0; i < pills.length; i++) {
        const kw = pills[i]!;
        if (i > 0) para.appendChild(document.createTextNode(" "));

        const head = document.createElement("strong");
        head.className = "card-fs-in-play-kw";
        if (kw.id) head.dataset.kw = kw.id;
        head.textContent = kw.label;

        const body = document.createTextNode(
          ` ${trimReminder(kw.reminder!)}`,
        );
        para.append(head, body);
      }
      details.appendChild(para);
    }

    // Short tactical tip (not a second full rules dump)
    if (data.effectSummary) {
      const tipLabel = document.createElement("p");
      tipLabel.className = "card-fs-section-label";
      tipLabel.textContent = pills.length ? "When to use" : "In play";
      const tip = document.createElement("p");
      tip.className = "card-fs-effect";
      tip.textContent = data.effectSummary;
      details.append(tipLabel, tip);
    }
  }

  private ensureDom(): void {
    if (this.root) return;

    const root = document.createElement("div");
    root.id = "card-fullscreen";
    root.className = "card-fullscreen hidden";
    root.setAttribute("role", "dialog");
    root.setAttribute("aria-modal", "true");
    root.setAttribute("aria-label", "Card full screen");

    const panel = document.createElement("div");
    panel.className = "card-fullscreen-panel";

    const close = document.createElement("button");
    close.type = "button";
    close.className = "card-fullscreen-close";
    close.setAttribute("aria-label", "Close full screen");
    close.textContent = "×";
    close.addEventListener("click", () => this.hide());

    const body = document.createElement("div");
    body.className = "card-fullscreen-body";

    const mount = document.createElement("div");
    mount.className = "card-fullscreen-mount";

    const details = document.createElement("div");
    details.className = "card-fullscreen-details hidden";

    body.append(mount, details);

    const hint = document.createElement("p");
    hint.className = "card-fullscreen-hint";
    hint.textContent = "Esc or click outside to close";

    panel.append(close, body, hint);
    root.appendChild(panel);

    // Close when clicking outside the study card, info panel, or keyword popup.
    // (Backdrop, panel chrome, hint, and gutters between cards all dismiss.)
    // Use pointerdown so it feels instant, then suppress the rest of the
    // gesture so the card under the backdrop does not open again.
    root.addEventListener("pointerdown", (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (t.closest(".card-fullscreen-close")) return;
      if (t.closest(".card-fs-kw-tip")) return;
      if (t.closest(".card-fs-study")) return;
      if (t.closest(".card-fullscreen-details")) return;
      if (t.closest(".card-fullscreen-mount .card")) return;
      e.preventDefault();
      e.stopPropagation();
      this.suppressClickThrough();
      this.hide();
    });

    document.body.appendChild(root);
    this.root = root;
    this.mount = mount;
    this.details = details;
  }

  private bindKeys(): void {
    if (this.keyHandler) return;
    this.keyHandler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        // First Esc closes pinned tip; second closes study
        if (this.tipEl && !this.tipEl.classList.contains("hidden")) {
          this.hideKeywordTip();
          return;
        }
        this.hide();
      }
    };
    window.addEventListener("keydown", this.keyHandler, true);
  }

  private unbindKeys(): void {
    if (!this.keyHandler) return;
    window.removeEventListener("keydown", this.keyHandler, true);
    this.keyHandler = null;
  }
}

/** Normalize reminder for inline paragraph flow (single terminal punctuation). */
function trimReminder(s: string): string {
  let t = s.replace(/\s+/g, " ").trim();
  if (!t) return "";
  // Collapse accidental double terminals; ensure one sentence end
  t = t.replace(/[.!?]+$/u, "");
  return `${t}.`;
}

/** Shared singleton for the app */
export const cardFullscreen = new CardFullscreen();
